import React, { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';

import Checkbox from '../../Checkbox/Checkbox';
import Span from '../../Texts/Span/Span';
import Link from '../../Link/Link';
import Button from '../../Button/Button';

import * as S from './TableCustomSelect.styles';

const createElement = (id, targetElementContainer) => {
  let element = targetElementContainer.querySelector(`#${id}`);
  if (targetElementContainer) {
    if (!element) {
      element = document.createElement('div');
      element.setAttribute('id', id);
    }
    targetElementContainer.appendChild(element);
  }
  return element;
};

const TableFooter = ({
  countSelected,
  maxSelected,
  itemsCurrent,
  onClick,
  icon,
  text,
  items,
  onClear,
  proxyOnClear,
}) => {
  const handleClick = () => {
    !!onClick && onClick(items);
  };

  const handleClear = () => {
    !!proxyOnClear && proxyOnClear();
    !!onClear && onClear(itemsCurrent);
  };

  return (
    !!countSelected && (
      <S.TableFooter>
        <S.LeftContent>
          {maxSelected ? (
            <Span type='2' variant='neutral' modifier='strong'>
              {countSelected} de {maxSelected} seleccionados
            </Span>
          ) : (
            <Span type='2' variant='neutral' modifier='strong'>
              {countSelected} seleccionado{countSelected > 1 && `s`}
            </Span>
          )}
          <Link type='2' variant='primary' onClick={handleClear}>
            Limpiar selección
          </Link>
        </S.LeftContent>
        <S.RightContent>
          {!!text && (
            <Button
              variant='primary'
              size='md'
              onClick={handleClick}
              icon={icon ? icon : 'download_outline'}
              iconAlign='right'
            >
              {text}
            </Button>
          )}
        </S.RightContent>
      </S.TableFooter>
    )
  );
};

const useTableSelectRow = ({ sizePerPage, selectRow, pageActive, data }) => {
  const {
    maxSelected = false,
    selectedList,
    nonSelectableChecked,
    labelAction = {},
    onSelect,
    onSelectAll,
  } = selectRow || {};

  const [items, setItems] = useState({
    selecteds: [],
    disableds: [],
    disabledSelecteds: [],
    selectedsIndex: [],
    disabledsIndex: [],
  });

  const [pageSelectRow, setPageSelectRow] = useState(pageActive);
  const [isMaxSelectable, setIsMaxSelectable] = useState(false);
  const [refTable, setRefTable] = useState(null);

  const tableSelectRow = selectRow
    ? {
        ...selectRow,
        mode: 'checkbox',
        headerColumnStyle: {
          width: '78px',
        },
        selectionRenderer: ({ checked, disabled }) => (
          <S.TableCheckbox>
            <Checkbox checked={checked} disabled={disabled} />
          </S.TableCheckbox>
        ),
        selectionHeaderRenderer: ({ checked, indeterminate }) => (
          <S.TableCheckbox>
            <Checkbox
              checked={checked || indeterminate}
              disabled={isMaxSelectable}
              isSelectAllCheck={indeterminate}
            />
          </S.TableCheckbox>
        ),
        onSelect: (row, isSelect) => {
          handleSelectRow({
            row,
            isSelect,
          });
        },
        onSelectAll: (isSelect, rows, _) => {
          rows &&
            handleSelectAllRow({
              rows,
              isSelect,
            });
        },
        selected: items.selectedsIndex,
        nonSelectable: items.disabledsIndex,
      }
    : undefined;

  const getIndexRows = useCallback(
    (rows) => (rows || []).map((row) => row?.index) || []
  );

  const getArrayNumber = useCallback(({ min, max }) =>
    Array.apply(null, { length: max })
      .map(Number.call, Number)
      .slice(min)
      .filter((index) => !getIndexRows(items.selecteds).includes(index))
  );

  const getItemsDisabledMax = () => {
    const min = parseInt(pageSelectRow - 1) * sizePerPage;
    const max = parseInt(pageSelectRow - 1) * sizePerPage + sizePerPage;
    return getArrayNumber({ min, max });
  };

  const getItemsCurrent = () => {
    const min = parseInt(pageSelectRow - 1) * sizePerPage;
    const max = parseInt(pageSelectRow - 1) * sizePerPage + sizePerPage;
    return (items?.selecteds || []).filter(
      (item) => item?.index >= min && item?.index < max
    );
  };

  const onClear = () =>
    setItems((items) => ({ ...items, selecteds: [], selectedsIndex: [] }));

  const handleSelectRow = useCallback(({ row, isSelect }) => {
    const selecteds = items.selecteds.filter(
      (current) => row.index !== current.index
    );

    const rowSelecteds = isSelect ? [...selecteds, row] : [...selecteds];

    setItems((items) => ({
      ...items,
      selecteds: rowSelecteds,
    }));

    onSelect && onSelect(rowSelecteds);
  });

  const handleSelectAllRow = useCallback(({ rows, isSelect }) => {
    const hasDisabledSelecteds =
      items.disabledSelecteds.length && !items.selecteds.length;
    isSelect = hasDisabledSelecteds || isSelect;
    rows = hasDisabledSelecteds
      ? data.filter((row) => {
          const disabledSelecteds = items.disabledSelecteds.find(
            (item) => row.index === item.index
          );
          return !disabledSelecteds && row;
        })
      : rows;

    const selecteds = items.selecteds.filter(
      (current) => !(getIndexRows(rows) || []).includes(current.index)
    );

    const resetSelecteds = rows.filter(
      (_, index) => items.selecteds.length + index + 1 <= maxSelected
    );

    const rowSelecteds = isSelect
      ? [...selecteds, ...resetSelecteds]
      : [...selecteds];

    setItems((items) => ({
      ...items,
      selecteds: rowSelecteds,
    }));
    onSelectAll && onSelectAll(rowSelecteds);
  });

  useEffect(() => {
    maxSelected && setIsMaxSelectable(maxSelected <= items.selecteds.length);
  }, [items.selecteds]);

  useEffect(() => {
    setItems((items) => ({
      ...items,
      disableds: isMaxSelectable ? getItemsDisabledMax() : [],
    }));
  }, [isMaxSelectable, pageSelectRow]);

  useEffect(() => {
    if (!!nonSelectableChecked || !!selectedList) {
      setItems((items) => ({
        ...items,
        disabledSelecteds: nonSelectableChecked,
        selecteds: selectedList,
      }));
    }
  }, [nonSelectableChecked, selectedList]);

  useEffect(() => {
    if (refTable) {
      ReactDOM.render(
        <React.StrictMode>
          <TableFooter
            isMaxSelectable={isMaxSelectable}
            maxSelected={maxSelected}
            countSelected={items.selecteds.length}
            items={items.selecteds}
            proxyOnClear={onClear}
            itemsCurrent={getItemsCurrent()}
            {...labelAction}
          />
        </React.StrictMode>,
        createElement(
          'react-bootstrap-table-footer',
          refTable.querySelector('.react-bootstrap-table')
        )
      );
    }
  }, [refTable, isMaxSelectable, items.selecteds]);

  useEffect(() => {
    setItems((items) => ({
      ...items,
      selectedsIndex: getIndexRows([
        ...items.selecteds,
        ...items.disabledSelecteds,
      ]),
    }));
  }, [items.selecteds, items.disabledSelecteds]);

  useEffect(() => {
    const disabledsIndex = getIndexRows(items.disabledSelecteds);
    setItems((items) => ({
      ...items,
      disabledsIndex: [...items.disableds, ...disabledsIndex],
    }));
  }, [items.disableds, items.disabledSelecteds, isMaxSelectable]);

  return [tableSelectRow, setPageSelectRow, setRefTable];
};

export default useTableSelectRow;
